import { useEffect, useMemo, useRef, useState } from 'react';

/* ROI calculator, ported from the host's Resource Optimizer module.

   THE MATHS IS COPIED EXACTLY — same divisor, same 4.33 weeks/month, same 85%
   recoverable share, same currency banding. Only the presentation is new. If
   the host's numbers ever change, change them here too or the two pages will
   quote different figures for the same inputs. */

const SALARY_OPTIONS = [
  { label: '₹30K', value: 30000 },
  { label: '₹50K', value: 50000 },
  { label: '₹80K', value: 80000 },
  { label: '₹1.2L+', value: 120000 },
];

const DEFAULTS = { employees: 87, salary: 50000, hours: 9 };

function formatCurrency(val) {
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
  return `₹${Math.round(val)}`;
}

/** Counts from the previous value to the new one, like the host's version. */
function AnimatedValue({ value, format }) {
  const [shown, setShown] = useState(value);
  const prev = useRef(value);

  useEffect(() => {
    const from = prev.current;
    const to = value;
    prev.current = value;
    if (from === to) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(to);
      return undefined;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / 600, 1);
      const eased = 1 - (1 - p) ** 3;
      setShown(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return format(shown);
}

export default function RoiCalculator() {
  const [employees, setEmployees] = useState(DEFAULTS.employees);
  const [salary, setSalary] = useState(DEFAULTS.salary);
  const [hours, setHours] = useState(DEFAULTS.hours);

  const { monthlyWaste, yearlyWaste, hoursRecoverable } = useMemo(() => {
    const hourlyRate = salary / (22 * 8);
    const weekly = employees * hours * hourlyRate;
    const monthly = weekly * 4.33;
    return {
      monthlyWaste: monthly,
      yearlyWaste: monthly * 12,
      hoursRecoverable: Math.round(employees * hours * 0.85),
    };
  }, [employees, salary, hours]);

  return (
    <section className="roic" id="calculator">
      <div className="container">
        <div className="roic-head">
          <span className="fl" style={{ color: '#fff', opacity: 0.85 }}>ROI CALCULATOR</span>
          <h2>Manual work is<br /><span className="roic-dim">a silent tax.</span></h2>
          <p>
            Every report typed, every approval chased, every spreadsheet updated by hand —
            quietly bleeding your payroll. Move the sliders. See your bill.
          </p>
        </div>

        <div className="roic-card">
          <div className="roic-inputs">
            <div className="roic-field">
              <div className="roic-frow">
                <label htmlFor="roi-emp">Number of employees</label>
                <span className="roic-val">{employees}</span>
              </div>
              <input
                id="roi-emp" type="range" min="10" max="500" step="1" value={employees}
                onChange={(e) => setEmployees(Number(e.target.value))}
                style={{ '--fill': `${((employees - 10) / 490) * 100}%` }}
              />
            </div>

            <div className="roic-field">
              <span className="roic-lab">Average monthly salary</span>
              <div className="roic-sals">
                {SALARY_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={salary === o.value ? 'on' : ''}
                    onClick={() => setSalary(o.value)}
                    aria-pressed={salary === o.value}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="roic-field">
              <div className="roic-frow">
                <label htmlFor="roi-hrs">
                  Hours/week on automatable tasks <span className="roic-hint">(per person)</span>
                </label>
                <span className="roic-val">{hours}h</span>
              </div>
              <input
                id="roi-hrs" type="range" min="1" max="35" step="1" value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                style={{ '--fill': `${((hours - 1) / 34) * 100}%` }}
              />
            </div>

            <div className="roic-cta">
              <p>Want to see exactly where the money is going?</p>
              <a className="btn-primary" href="/resource-optimizer/book">Book your free audit</a>
            </div>
          </div>

          <div className="roic-out">
            <div className="roic-waste">
              <span className="roic-wlab">YOUR ESTIMATED WASTE</span>
              <span className="roic-wval"><AnimatedValue value={monthlyWaste} format={formatCurrency} /></span>
              <span className="roic-wsub">wasted every month</span>
              <span className="roic-wyear">
                That&apos;s <b><AnimatedValue value={yearlyWaste} format={formatCurrency} />/year</b> down the drain
              </span>
            </div>
            <div className="roic-stats">
              <div className="roic-stat">
                <span className="v"><AnimatedValue value={hoursRecoverable} format={(v) => `${Math.round(v)}h`} /></span>
                <span className="l">recoverable<br />hours/week</span>
              </div>
              <div className="roic-stat">
                <span className="v">85%</span>
                <span className="l">typically<br />automatable</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
